// =====================================================================
// TemplatePreview — editor de template_text com detecao automatica de
// placeholders ({nome}, {data}, etc) + preview renderizado.
//
// Fase 3, S38. Usado na aba Templates (clinic_message_templates).
//
// Props:
//   value: string (template_text)
//   onChange(text): callback
//   variables: object (defaults pros placeholders detectados)
//   onVariablesChange(obj): callback
//
// Comportamento:
//   - Detecta placeholders via regex /\{([a-z0-9_]+)\}/gi
//   - Mostra lista dos detectados + input pra mock value (sincroniza com
//     `variables`)
//   - Renderiza preview: substitui {key} pelo mock value
//   - Variaveis sem mock value aparecem destacadas no preview
// =====================================================================
import React, { useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  TextField,
  Typography,
  Paper,
  Chip,
  Grid,
  Divider
} from "@material-ui/core";

const PLACEHOLDER_REGEX = /\{([a-z0-9_]+)\}/gi;

const useStyles = makeStyles((theme) => ({
  root: { display: "flex", flexDirection: "column", gap: theme.spacing(2) },
  bodyRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  preview: {
    padding: theme.spacing(1.5, 2),
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.04)"
        : "rgba(0,0,0,0.03)",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    minHeight: 120,
    fontFamily: "inherit",
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
    whiteSpace: "pre-wrap",
    lineHeight: 1.5
  },
  previewLabel: {
    fontSize: "0.6875rem",
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: theme.spacing(0.75)
  },
  unfilledPlaceholder: {
    background: `${theme.palette.warning.main}30`,
    color: theme.palette.warning.dark || theme.palette.warning.main,
    borderRadius: 4,
    padding: "1px 6px",
    fontWeight: 600,
    fontSize: "0.8125rem"
  },
  filledPlaceholder: {
    background: `${theme.palette.primary.main}22`,
    color: theme.palette.primary.main,
    borderRadius: 4,
    padding: "1px 6px",
    fontWeight: 600
  },
  varsTitle: {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: theme.spacing(1)
  },
  varsEmpty: {
    fontSize: "0.8125rem",
    color: theme.palette.text.disabled,
    fontStyle: "italic"
  },
  varInputRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  varKeyChip: {
    fontFamily: "monospace",
    fontWeight: 600,
    minWidth: 120,
    justifyContent: "flex-start"
  }
}));

// Extrai placeholders unicos preservando ordem de aparicao
const extractPlaceholders = (text) => {
  const set = new Set();
  if (!text) return [];
  const re = new RegExp(PLACEHOLDER_REGEX.source, "gi");
  let m;
  while ((m = re.exec(text)) !== null) {
    set.add(m[1].toLowerCase());
  }
  return Array.from(set);
};

// Renderiza o template substituindo placeholders. Retorna array de
// segments pra que possamos estilizar diferentemente os preenchidos
// vs nao-preenchidos.
const renderSegments = (text, variables) => {
  if (!text) return [];
  const segments = [];
  const re = new RegExp(PLACEHOLDER_REGEX.source, "gi");
  let lastIdx = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIdx) {
      segments.push({ type: "text", value: text.slice(lastIdx, m.index) });
    }
    const key = m[1].toLowerCase();
    const val = variables && variables[key];
    if (val !== undefined && val !== null && val !== "") {
      segments.push({ type: "filled", key, value: String(val) });
    } else {
      segments.push({ type: "unfilled", key, value: `{${m[1]}}` });
    }
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) {
    segments.push({ type: "text", value: text.slice(lastIdx) });
  }
  return segments;
};

const TemplatePreview = ({
  value,
  onChange,
  variables,
  onVariablesChange
}) => {
  const classes = useStyles();

  const placeholders = useMemo(() => extractPlaceholders(value), [value]);
  const segments = useMemo(
    () => renderSegments(value, variables),
    [value, variables]
  );

  const setVar = (key, v) => {
    const next = { ...(variables || {}), [key]: v };
    onVariablesChange && onVariablesChange(next);
  };

  return (
    <div className={classes.root}>
      <div className={classes.bodyRow}>
        <TextField
          label="Texto do template"
          variant="outlined"
          size="small"
          multiline
          rows={8}
          fullWidth
          value={value || ""}
          onChange={(e) => onChange && onChange(e.target.value)}
          placeholder={"Olá {nome}, sua consulta foi agendada pra {data} às {hora}."}
          helperText="Use {placeholder} pra campos dinâmicos. Ex: {nome}, {data}, {hora}"
        />
        <div>
          <Typography className={classes.previewLabel}>
            Pré-visualização
          </Typography>
          <Paper className={classes.preview} elevation={0}>
            {segments.length === 0 ? (
              <span style={{ opacity: 0.5 }}>
                Digite o template no editor ao lado.
              </span>
            ) : (
              segments.map((s, i) => {
                if (s.type === "text") return <span key={i}>{s.value}</span>;
                if (s.type === "filled") {
                  return (
                    <span key={i} className={classes.filledPlaceholder}>
                      {s.value}
                    </span>
                  );
                }
                return (
                  <span key={i} className={classes.unfilledPlaceholder}>
                    {s.value}
                  </span>
                );
              })
            )}
          </Paper>
        </div>
      </div>

      <Divider />

      <div>
        <Typography className={classes.varsTitle}>
          Variáveis detectadas ({placeholders.length})
        </Typography>
        {placeholders.length === 0 ? (
          <Typography className={classes.varsEmpty}>
            Nenhum placeholder no texto. Adicione algo como{" "}
            <code>{"{nome}"}</code> pra começar.
          </Typography>
        ) : (
          <Grid container spacing={1}>
            {placeholders.map((k) => (
              <Grid item xs={12} md={6} key={k}>
                <Box className={classes.varInputRow}>
                  <Chip
                    label={`{${k}}`}
                    size="small"
                    variant="outlined"
                    className={classes.varKeyChip}
                  />
                  <TextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    placeholder={`Valor de exemplo pra ${k}`}
                    value={(variables && variables[k]) || ""}
                    onChange={(e) => setVar(k, e.target.value)}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </div>
    </div>
  );
};

export default TemplatePreview;
